import { useQueries, useQuery } from '@tanstack/react-query';
import { useSyncExternalStore } from 'react';

import {
  deriveCan,
  resolveCurrentUserRole,
} from '@/features/workspace/workspace-access.js';
import {
  workspaceMembersQueryOptions,
  workspacesQueryOptions,
  type Workspace,
  type WorkspaceMember,
} from '@/lib/api/workspaces.js';
import {
  getState as getIdentityState,
  subscribe as subscribeIdentity,
} from '@/store/identity-store.js';

/** A workspace a skill can be copied into. */
export interface CopyTarget {
  id: string;
  name: string;
}

/**
 * The workspaces a skill can be copied into: shared ones, other than the skill's own, where the
 * caller is an owner or editor — the server's authoring rule for a workspace skill. Sorted by name.
 */
export function copyTargetsFrom(args: {
  workspaces: Workspace[] | undefined;
  membersByWorkspace: Map<string, WorkspaceMember[] | undefined>;
  currentUserId: string | null;
  isAdmin: boolean;
  sourceWorkspaceId: string | null;
}): CopyTarget[] {
  const candidates = (args.workspaces ?? []).filter(
    (workspace) =>
      workspace.kind === 'shared' && workspace.id !== args.sourceWorkspaceId,
  );
  const authorable = candidates.filter((workspace) => {
    const role = resolveCurrentUserRole({
      workspace,
      members: args.membersByWorkspace.get(workspace.id),
      currentUserId: args.currentUserId,
      isAdmin: args.isAdmin,
    });
    return deriveCan(role).manageSettings;
  });
  return authorable
    .map((workspace) => ({ id: workspace.id, name: workspace.name }))
    .sort((left, right) => left.name.localeCompare(right.name));
}

/**
 * `copyTargetsFrom` over the caller's live workspaces. Member directories are fetched only while
 * `enabled` (the copy dialog is open), one per shared workspace, since the workspace list carries no
 * role of its own. `failed` is true when a directory could not be read, so its workspace may be
 * missing from `targets` for that reason rather than for the caller's role.
 */
export function useSkillCopyTargets(
  sourceWorkspaceId: string | null,
  enabled: boolean,
): { targets: CopyTarget[]; loading: boolean; failed: boolean } {
  const identity = useSyncExternalStore(
    subscribeIdentity,
    getIdentityState,
    getIdentityState,
  );
  const { data: workspaces } = useQuery(workspacesQueryOptions());
  const shared = (workspaces ?? []).filter(
    (workspace) =>
      workspace.kind === 'shared' && workspace.id !== sourceWorkspaceId,
  );
  const memberQueries = useQueries({
    queries: shared.map((workspace) => ({
      ...workspaceMembersQueryOptions(workspace.id),
      enabled,
    })),
  });
  const membersByWorkspace = new Map(
    shared.map((workspace, index) => [
      workspace.id,
      memberQueries[index]?.data,
    ]),
  );
  return {
    targets: copyTargetsFrom({
      workspaces,
      membersByWorkspace,
      currentUserId: identity.identity?.userId ?? null,
      isAdmin: (identity.identity?.roles ?? []).includes('admin'),
      sourceWorkspaceId,
    }),
    loading:
      workspaces === undefined ||
      memberQueries.some((query) => query.isLoading),
    failed: memberQueries.some(
      (query) => query.isError && query.data === undefined,
    ),
  };
}
